import { useState } from 'react';
import { Share2, Check, Copy, X } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { showToast } from '@/stores/toast-store';
import { cn, copyToClipboard } from '@/lib/utils';

export function ShareButton() {
  const { har, fileName } = useHARStore();
  const [isSharing, setIsSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!har) return null;

  const handleShare = async () => {
    setIsSharing(true);
    setShareError(null);
    
    try {
      const content = JSON.stringify(har);
      const blob = new Blob([content], { type: 'application/json' });
      const formData = new FormData();
      formData.append('file', blob, fileName || 'share.har');
      formData.append('fileName', fileName || 'share.har');
      
      // Use local API in development, production API in production
      const API_URL = import.meta.env.DEV
        ? 'http://localhost:8787'
        : (import.meta.env.VITE_API_URL || 'https://har-insight-api.thelazysre.workers.dev');
      const response = await fetch(`${API_URL}/share`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create share' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      
      const data = await response.json();
      const url = `${window.location.origin}/s/${data.id}`;
      setShareUrl(url);
    } catch (error) {
      setShareError(error instanceof Error ? error.message : 'Failed to share');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopy = async () => {
    if (shareUrl) {
      await copyToClipboard(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast('Share link copied to clipboard!', 'success');
    }
  };

  const handleClose = () => {
    setShareUrl(null);
    setShareError(null);
  };

  if (shareUrl) {
    return (
      <div className="flex items-center gap-2 bg-brand-50 dark:bg-brand-500/10 px-3 py-1.5 rounded-lg">
        <span className="text-sm text-brand-700 dark:text-brand-300 truncate max-w-[200px]">
          {shareUrl}
        </span>
        <button
          onClick={handleCopy}
          className="p-1 hover:bg-brand-100 dark:hover:bg-brand-500/20 rounded transition-colors"
          title="Copy link"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 text-brand-600 dark:text-brand-400" />
          )}
        </button>
        <button
          onClick={handleClose}
          className="p-1 hover:bg-brand-100 dark:hover:bg-brand-500/20 rounded transition-colors"
          title="Close"
        >
          <X className="w-4 h-4 text-brand-600 dark:text-brand-400" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {shareError && (
        <span className="text-sm text-red-600 dark:text-red-400 max-w-[150px] truncate" title={shareError}>
          {shareError}
        </span>
      )}
      <button
        onClick={handleShare}
        disabled={isSharing}
        className={cn(
          'btn-ghost flex items-center gap-2',
          isSharing && 'opacity-50 cursor-not-allowed'
        )}
        title="Share HAR file"
      >
        <Share2 className="w-4 h-4" />
        <span className="hidden sm:inline">{isSharing ? 'Sharing...' : 'Share'}</span>
      </button>
    </div>
  );
}
